import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  Users,
  Building2,
  Shield,
  Award,
  MousePointerClick,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function GallerySection() {
  const [hoveredImage, setHoveredImage] = useState(null);
  const navigate = useNavigate();

  const sections = [
    {
      id: "training",
      icon: <Users className="w-16 h-16 text-green-600" />,
      fallbackGradient: "from-green-400 to-teal-400",
      title: "Pelatihan Personel",
      category: "Training",
      description:
        "Pelatihan intensif untuk peningkatan profesionalisme personel.",
    },
    {
      id: "project",
      icon: <Building2 className="w-16 h-16 text-green-600" />,
      fallbackGradient: "from-teal-400 to-green-400",
      title: "Pengawasan Proyek",
      category: "Operations",
      description: "Manajemen dan keamanan proyek lapangan secara menyeluruh.",
    },
    {
      id: "service",
      icon: <Shield className="w-16 h-16 text-green-600" />,
      fallbackGradient: "from-green-500 to-teal-400",
      title: "Peralatan Keamanan",
      category: "Service",
      description: "Implementasi teknologi keamanan modern dan efisien.",
    },
    {
      id: "certificate",
      icon: <Award className="w-16 h-16 text-green-600" />,
      fallbackGradient: "from-green-400 to-lime-400",
      title: "Sertifikasi & Penghargaan",
      category: "Certificate",
      description: "Pengakuan resmi atas kualitas dan standar pelayanan kami.",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" },
    }),
  };

  return (
    <section className="relative bg-gradient-to-br from-white via-white/95 to-white py-32 px-6 md:px-16 overflow-hidden">
      {/* 🌿 Background Effects */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* ✴️ Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-10 text-center mb-20 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100/10 border border-green-200/30 rounded-full backdrop-blur-sm mb-6"
        >
          <Camera className="w-4 h-4 text-green-500" />
          <span className="text-green-500 text-sm font-mono uppercase tracking-wider">
            Gallery
          </span>
        </motion.div>

        <motion.h2
          whileHover={{ scale: 1.02 }}
          className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight"
        >
          <span className="text-gray-900">Bukti </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-teal-400 to-green-600">
            Kualitas
          </span>
        </motion.h2>

        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"
        ></motion.div>

        <p className="text-lg text-gray-600 leading-relaxed font-light">
          Lihat standar kerja, personel terlatih, dan komitmen kami pada setiap
          proyek secara visual.
        </p>
      </motion.div>

      {/* 🖼️ Cards Grid */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {sections.map((item, index) => {
            const isHovered = hoveredImage === index;

            return (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={index}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                onMouseEnter={() => setHoveredImage(index)}
                onMouseLeave={() => setHoveredImage(null)}
                onClick={() => navigate(`/gallery/${item.id}`)}
                className="group relative cursor-pointer"
              >
                {/* 🌟 Glow Effect */}
                <motion.div
                  animate={{ opacity: [0, 0.25, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className={`absolute -inset-1 bg-gradient-to-r ${item.fallbackGradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-40 transition duration-500`}
                ></motion.div>

                {/* 📦 Card */}
                <div className="relative h-80 rounded-3xl flex flex-col items-center justify-center border border-gray-200 bg-white/90 backdrop-blur-sm shadow-md transition-all duration-500 group-hover:shadow-2xl overflow-hidden">
                  {/* === TAMBAHAN: Info CTA di setiap card === */}
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.15 + 0.5 }} // Muncul setelah card fade-up
                    className="absolute top-4 right-4 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm border border-green-200"
                  >
                    <MousePointerClick className="w-3 h-3" />
                    <span>Lihat Detail</span>
                  </motion.div>
                  {/* ========================================= */}

                  {/* 🧩 Icon */}
                  <motion.div
                    animate={{
                      y: isHovered ? [0, -6, 0] : 0,
                      scale: isHovered ? 1.1 : 1,
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: isHovered ? Infinity : 0,
                    }}
                    className="mb-4"
                  >
                    {item.icon}
                  </motion.div>

                  {/* 🏷️ Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                    {item.title}
                  </h3>

                  {/* 📖 Description */}
                  <p className="text-gray-600 text-sm text-center max-w-[220px] mx-auto leading-relaxed">
                    {item.description}
                  </p>

                  {/* 🖱️ Klik Hint (yang sudah ada) */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{
                      opacity: isHovered ? 1 : 0,
                      y: isHovered ? 0 : 8,
                    }}
                    transition={{ duration: 0.4 }}
                    className="absolute bottom-5 flex items-center gap-2 text-xs font-medium text-green-600"
                  >
                    <MousePointerClick className="w-4 h-4" />
                    Klik untuk melihat
                  </motion.div>

                  {/* 🔹 Accent Line */}
                  <motion.div
                    animate={{ scaleX: isHovered ? 1 : 0 }}
                    transition={{ duration: 0.5 }}
                    className={`absolute bottom-0 left-0 right-0 h-1 origin-left bg-gradient-to-r ${item.fallbackGradient}`}
                  ></motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default GallerySection;
